<template>
  <div v-show="showScheduleDetails">
    <v-card class="mx-2">
      <v-card-text class="mb-4">
        <div v-if="loading">
          <v-progress-circular indeterminate color="primary" class="mx-auto d-block mt-4" />
        </div>
        <div v-else>
          <div v-for="(product, productIndex) in sortedProductList" :key="`product-${product.id}-${productIndex}`">
            <v-data-table :headers="headers" :items="product.winningPatterns" class="elevation-2 mb-4"
              hide-default-footer :item-key="item => item.id">
              <template v-slot:top>
                <v-toolbar flat class="primary white--text">
                  <v-row>
                    <v-col cols="6" md="2">
                      <v-toolbar-title class="ml-2">{{ product.productTemplateDto.title }}</v-toolbar-title>
                    </v-col>
                    <v-spacer />
                    <v-col cols="2">
                      <v-row>
                        <v-tooltip bottom v-if="$vuetify.breakpoint.smAndUp">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon dark v-bind="attrs" v-on="on" @click="copyText(product.id)">
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <span>Product Id: {{ product.id }} (Click to copy)</span>
                        </v-tooltip>
                        <v-col class="pr-0">
                          <v-text-field background-color="primary" solo dense flat hide-details readonly reverse dark
                            value="Price" />
                        </v-col>
                        <v-col class="pl-0 mt-1">
                          <currency-field solo dense hide-details dark v-model="product.price" />
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="2">
                      <v-btn color="primary" dark class="mb-2" @click="addLineItem(product)"
                        :disabled="product.winningPatterns.length >= 4">
                        New Line Item
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-toolbar>
              </template>
              <template v-slot:[`item.intOne`]="{ item }">
                <v-text-field v-model="item.intOne" type="number"></v-text-field>
              </template>
              <template v-slot:[`item.intTwo`]="{ item }">
                <v-text-field class="payoutField" v-model="item.intTwo" type="number" prefix="€"></v-text-field>
              </template>
              <template v-slot:[`item.lineItem`]="{ index }">
                <div>{{ index + 1 }}</div>
              </template>
              <template v-slot:[`item.winningPattern`]="{ item, index }">
                <v-select label="Winning Pattern"
                  :items="availableWinningPatterns(product.winningPatterns, index, item)" v-model="item.winningPattern"
                  dense />
              </template>
            </v-data-table>
          </div>
          <v-row class="mt-2">
            <v-col class="text-right pt-3">
              <v-btn color="primary" class="mr-4" @click="handleSave" :disabled="saving"> Save
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CurrencyField from "@/components/common/CurrencyField.vue";

export default {
  components: { CurrencyField },
  name: "FOBProducts",
  mounted() {
    this.initializeComponent();
  },
  data() {
    return {
      loading: false,
      selectedSchedule: null,
      saving: false,
      headers: [
        // {
        //   text: "Line",
        //   value: "lineItem",
        //   width: "10%"
        // },
        {
          text: "Winning Pattern",
          value: "winningPattern",
          width: "25%"
        },
        {
          text: "Ordinal Number",
          value: "intOne",
          width: "10%"
        },
        {
          text: "Payout",
          value: "intTwo",
        }
      ],
    };
  },
  methods: {
    ...mapActions([
      "fetchProductsForSchedule",
      "saveProductDetails",
      "setCurrentPage"
    ]),
    async handleSave() {
      if (this.saving) return;
      this.saving = true;
      try {
        await this.saveProductDetails(this.getProductList);
      } catch (error) {
        console.error("Save failed:", error);
      } finally {
        this.saving = false;
      }
    },
    initializeComponent() {
      this.loading = true;
      const scheduleId = this.$route.params.scheduleId || this.getSelectedSchedule;

      if (scheduleId) {
        this.selectedSchedule = this.getScheduleList.find(schedule => schedule && schedule.id == scheduleId);

        if (this.selectedSchedule) {
          this.fetchProductsForSchedule(this.selectedSchedule.id)
            .catch(error => {
              console.error("Error fetching products:", error);
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          console.warn("Schedule not found with ID:", scheduleId);
          this.loading = false;
        }
      } else {
        console.warn("No schedule ID available");
        this.loading = false;
      }

      this.setCurrentPage('Edit Schedule');
    },
    availableWinningPatterns(winningPatterns = [], currentIndex, item) {
      const allPatterns = ['ONE_LINE', 'TWO_LINE', 'FULL_HOUSE', 'JACKPOT'];

      // If the current item's winningPattern is null/empty, show only unselected patterns
      const selectedPatterns = winningPatterns
        .filter((_, index) => index !== currentIndex)
        .map(wp => wp.winningPattern)
        .filter(Boolean); // remove null/undefined

      return allPatterns.filter(pattern =>
        !selectedPatterns.includes(pattern) || item.winningPattern === pattern
      );
    },
    addLineItem(product) {
      product.winningPatterns.push({
        id: Date.now() + Math.random(),
        winningPattern: null,
        intOne: 0,
        intTwo: 0
      });
    },
    copyText(text) {
      navigator.clipboard.writeText(text);
    },
  },
  computed: {

    ...mapGetters(["getScheduleList", "getProductList", "getSelectedSchedule"]),
    showScheduleDetails() {
      return !!this.selectedSchedule;
    },
    sortedProductList() {
      return this.getProductList.slice().sort((a, b) => {
        const orderA = a.games?.[0]?.gameOrder ?? 0;
        const orderB = b.games?.[0]?.gameOrder ?? 0;
        return orderA - orderB;
      });
    }
  },
};
</script>

<style scoped>
.square {
  width: 1.25vw;
  height: 1.25vw;
  min-width: 10px;
  min-height: 10px;
  border: rgb(73, 73, 73);
  border-style: solid;
  border-radius: 0.5px;
}

.inline-block {
  display: inline-block;
}

.payoutField {
  color: black !important;
}
</style>
